import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getKey } from "../../helpers/sessionKey";
import Button from "../Reusable/Button";
import Card from "../Reusable/Card";
import { default as Modal, default as PopupModal } from "../Reusable/Modal";
import CreateItems from "./CreateItems";
import CreateParty from "./CreateParty";
import Form from "./Form";
import { ServiceSalesSchema, TradingSalesSchema } from "../../validation/Sales";

const EditDraftPurchase = (props: any) => {
  const {
    handleEdit,
    isLoading,
    data,
    openPrintPopup,
    companyData,
    handleRevert,
    isRevertLoading,
    handlePrint,
    handleCancel,
    setOpenPrintPopup,
    isPrintLoading,
  } = props;

  const navigate = useNavigate();
  const [openUserFormPopup, setOpenUserFormPopup] = useState(false);
  const [isOpenItemsPopup, setIsOpenItemsPopup] = useState(false);

  const {
    control,
    register,
    setValue,
    setError,
    getValues,
    reset,
    clearErrors,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<any>({
    resolver: yupResolver(
      companyData?.type === "Trading" ? TradingSalesSchema : ServiceSalesSchema
    ),
    defaultValues: async () => {
      return {
        ...data,
        default_sales_account: companyData?.default_sales_account?.name,
        bill_number: data?.bill_number,
        items: data?.items.map((item) => ({
          account: {
            ...item?.account,
            is_taxable: item?.is_taxable,
            previous_quantity: item?.quantity,
          },
          quantity: item?.quantity,
          previous_quantity: item?.quantity,
          rate: Number(item?.rate),
          amount: item?.total,
          hs_code: item?.account?.hs_code,
          discount: item?.discount,
          unit: item?.account?.unit?.name,
        })),
      };
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        default_sales_account: companyData?.default_sales_account?.name,
        bill_number: data?.bill_number,
        items: data?.items.map((item) => ({
          account: {
            ...item?.account,
            is_taxable: item?.is_taxable,
            previous_quantity: item?.quantity,
          },
          quantity: item?.quantity,
          previous_quantity: item?.quantity,
          rate: Number(item?.rate),
          amount: item?.total,
          hs_code: item?.account?.hs_code,
          discount: item?.discount,
          unit: item?.account?.unit?.name,
        })),
      });
    }
  }, [data, companyData, reset]);
  const items = watch("items");
  const vat_tax = watch("tax_rate");

  useEffect(() => {
    if (!Array.isArray(items)) return;

    let taxableTotal = 0;
    let nonTaxableTotal = 0;
    let itemsValue = 0;
    let totalDiscount = 0;

    items.forEach((item, index) => {
      const amount = Number(item?.amount) || 0;
      const discount = Number(item?.discount) || 0;
      const isTaxable = Boolean(item?.is_taxable);
      itemsValue += amount;
      const amountAfterDiscount = Math.max(amount - discount, 0);
      totalDiscount += discount;

      if (isTaxable) {
        taxableTotal += amountAfterDiscount;
      } else {
        nonTaxableTotal += amountAfterDiscount;
      }
    });

    const netAmount = taxableTotal + nonTaxableTotal;
    const vatTax = taxableTotal * (Number(vat_tax) / 100);
    const totalAmount = netAmount + vatTax;

    setValue("items_value", itemsValue.toFixed(2));
    setValue("vat_tax_amount", vatTax.toFixed(2));
    setValue("total_amount", totalAmount.toFixed(2));
    setValue("taxable_amount", taxableTotal.toFixed(2));
    setValue("non_taxable_amount", nonTaxableTotal.toFixed(2));
    setValue("total_discount", totalDiscount.toFixed(2));
  }, [JSON.stringify(items), setValue, vat_tax]);

  const timeoutRef = useRef<any>(null);
  const isSuperUser = getKey("is_superuser");
  const adminOnly = data?.is_draft;

  useEffect(() => {
    if (!Array.isArray(items) || !data?.is_draft) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      items.forEach((item, index) => {
        const quantity = Number(item.quantity);
        const rate = Number(item.rate);
        const discount = Number(item.discount);
        const amount = Number(item.amount);
        const availableQuantity = Number(item.available_quantity);
        const maintainStock = item.is_maintain_stock;
        const minPurchaseRate = Number(item.last_purchase_price);
        let hasError = false;
        if (quantity > availableQuantity && maintainStock) {
          setError(`items.${index}.quantity`, {
            type: "manual",
            message: `Only ${availableQuantity} units available`,
          });
          hasError = true;
        } else {
          clearErrors(`items.${index}.quantity`);
        }

        if (rate < minPurchaseRate && rate) {
          setError(`items.${index}.rate`, {
            type: "manual",
            message: `Rate cannot be less than minimum purchase price (${minPurchaseRate})`,
          });
          hasError = true;
        } else {
          clearErrors(`items.${index}.rate`);
        }
        if (discount > 0) {
          const totalAmount = amount - discount;
          const discountedRate = totalAmount / quantity;
          if (discountedRate < minPurchaseRate) {
            setError(`items.${index}.discount`, {
              type: "manual",
              message: `Discounted rate (${discountedRate}) is below minimum purchase price (${minPurchaseRate})`,
            });
            hasError = true;
          } else {
            clearErrors(`items.${index}.discount`);
          }
        } else {
          clearErrors(`items.${index}.discount`);
        }
      });
    }, 500);

    return () => clearTimeout(timeoutRef.current);
  }, [JSON.stringify(items), data]);

  const handleClosePrint = () => {
    setOpenPrintPopup(false);
    navigate("/accounting/sales");
  };

  return (
    <>
      <Card>
        <form
          onSubmit={handleSubmit((data) => handleEdit(data, false, setError))}
        >
          <Form
            control={control}
            register={register}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            companyData={companyData}
            isEdit={true}
            isDraft={adminOnly}
            setOpenUserFormPopup={setOpenUserFormPopup}
            setIsOpenItemsPopup={setIsOpenItemsPopup}
            clearErrors={clearErrors}
            watch={watch}
          />
          <div className={tw`w-full flex gap-x-4 justify-end px-4`}>
            <div className={tw`w-24 `}></div>
            {data?.is_draft ? (
              <div className={tw`w-24 `}>
                <Button
                  type="button"
                  disabled={!adminOnly}
                  label="Save to Draft"
                  isLoading={isLoading}
                  onClick={handleSubmit((data) =>
                    handleEdit(data, true, setError)
                  )}
                />
              </div>
            ) : (
              <div className={tw`w-24 `}>
                <Button
                  type="button"
                  // disabled={!adminOnly}
                  label="Revert"
                  isLoading={isRevertLoading}
                  onClick={() => handleRevert(setError)}
                />
              </div>
            )}
            <div className={tw`w-24`}>
              <Button
                label="Submit"
                disabled={!data?.is_draft}
                isLoading={isLoading}
              />
            </div>
          </div>
        </form>
      </Card>
      <Modal
        title={"Create Party"}
        isOpen={openUserFormPopup}
        setIsOpen={setOpenUserFormPopup}
      >
        <CreateParty
          setOpenUserFormPopup={setOpenUserFormPopup}
          setParty={setValue}
        />
      </Modal>
      <PopupModal
        isOpen={openPrintPopup}
        setIsOpen={handleClosePrint}
        title={"Print"}
      >
        <form action="#" className={tw`w-[600px]`}>
          <Button
            label={"Print "}
            type="button"
            isLoading={isPrintLoading}
            disabled={isPrintLoading}
            onClick={handlePrint}
            autoFocus
          />
          <Button
            type="button"
            label={"Close"}
            onClick={() => handleClosePrint()}
          />
        </form>
      </PopupModal>
      <PopupModal
        isOpen={isOpenItemsPopup}
        setIsOpen={setIsOpenItemsPopup}
        title="Create Items"
      >
        <CreateItems setIsOpenItemsPopup={setIsOpenItemsPopup} />
      </PopupModal>
    </>
  );
};

export default EditDraftPurchase;

